"use client";

// components/public/GoPrefetchAnchor.tsx
// Plain <a> that prefetches its own /go hop on hover and keyboard focus
// (Priority 320), so the redirect route is warm by the time the visitor
// clicks. Drop-in for the existing outbound CTA anchors: every prop
// (href, target, rel, aria-label, className, onClick) passes straight
// through, so link contract, styling and click tracking are unchanged.
//
// Nothing is prefetched on mount, and nothing but a same-origin /go path is
// ever prefetched — lib/go-prefetch.ts decides. Each path is prefetched at
// most once per page load, however many CTAs point at it or however often
// they are hovered.
//
// Source-page attribution: an internal /go href carries ?src=<current path>
// (lib/go-source.ts) in the server-rendered HTML, so the redirect can record
// which page the click came from — rel="noreferrer" strips the Referer that
// used to carry it. usePathname resolves during prerender, so the server and
// client hrefs match (no hydration mismatch), and rel/target/sponsored
// semantics are untouched. The path is path-only: never a query string.

import type { ComponentPropsWithoutRef, FocusEvent, MouseEvent } from "react";
import { usePathname, useRouter } from "next/navigation";
import { goPrefetchPath } from "@/lib/go-prefetch";
import { withGoSource } from "@/lib/go-source";

const prefetched = new Set<string>();

type GoPrefetchAnchorProps = Omit<ComponentPropsWithoutRef<"a">, "href"> & {
  href: string;
};

export default function GoPrefetchAnchor({
  href,
  onMouseEnter,
  onFocus,
  children,
  ...rest
}: GoPrefetchAnchorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const resolvedHref = pathname ? withGoSource(href, pathname) : href;

  const prefetchOnce = () => {
    const path = goPrefetchPath(resolvedHref);
    if (!path || prefetched.has(path)) return;
    prefetched.add(path);
    router.prefetch(path);
  };

  return (
    <a
      {...rest}
      href={resolvedHref}
      onMouseEnter={(event: MouseEvent<HTMLAnchorElement>) => {
        prefetchOnce();
        onMouseEnter?.(event);
      }}
      onFocus={(event: FocusEvent<HTMLAnchorElement>) => {
        prefetchOnce();
        onFocus?.(event);
      }}
    >
      {children}
    </a>
  );
}
